import { uuidModule } from "@/modules/uuid";
import {
  AssistantSkillOrigin,
  AssistantSkillStatus,
  type AssistantSkillModelData,
} from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import type { ICreateAssistantSkillOperation } from "@/store/sync/operations/types";
import type { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";

export class CreateAssistantSkillOperation extends BaseSyncOperation<ICreateAssistantSkillOperation> {
  get operationKind(): "CREATE_ASSISTANT_SKILL" {
    return "CREATE_ASSISTANT_SKILL";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const modelData: AssistantSkillModelData = {
      space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
      assistant_id: this.payload.assistant_id,
      name: this.payload.name,
      when_to_use: this.payload.when_to_use,
      instructions: this.payload.instructions,
      status: AssistantSkillStatus.Active,
      origin: this.payload.origin ?? AssistantSkillOrigin.UserCreated,
      public_id: null,
      created_at: this.committedAt,
      updated_at: this.committedAt,
    };

    return [
      {
        optimistic_update_id: uuidModule.generate(),
        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: {
          model_id: this.payload.id,
          model_kind: "ASSISTANT_SKILL",
          model_version: 1,
          model_data: modelData,
          model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
        },
      } as OptimisticSyncUpdate<AssistantSkillModelData>,
    ];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantSkills.recompute(this.payload.id);
  }
}
